import React, { useRef, Suspense, useCallback, useEffect } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { useGLTF, OrbitControls, Environment } from '@react-three/drei';
import { useNavigate } from 'react-router-dom';
import * as THREE from 'three';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleNodes, faDna, faBaby, faRibbon, faMicroscope, faMobileAlt, faChevronRight, faChevronDown, faPlayCircle
} from '@fortawesome/free-solid-svg-icons';
import { motion, useScroll, useTransform } from 'framer-motion';
import { platformsConfig } from '../platformConfig';
import './Hero.css';
import logoImage from '../PA.svg';

const SEARCH_API_URL = 'https://links.szapfs.org';

function HumanBodyModel({ position, scale = 1.5 }) {
  const gltf = useGLTF(`https://webserver-api.cloudxomics.com/api/oncorisk/assets/human-body.glb`);
  const meshRef = useRef();

  // Memoize the wireframe scene to prevent re-creation on every render
  const wireframeScene = React.useMemo(() => {
    if (gltf.scene) {
      const clonedScene = gltf.scene.clone();
      const thresholdAngle = 100 * (Math.PI / 180);

      clonedScene.traverse((object) => {
        if (object.isMesh) {
          const edgesGeom = new THREE.EdgesGeometry(object.geometry, thresholdAngle);
          const lineMat = new THREE.LineBasicMaterial({
            color: 'white',
            transparent: true,
            opacity: 0.3,
          });
          const wireframe = new THREE.LineSegments(edgesGeom, lineMat);
          object.add(wireframe);
          object.material.visible = false;
        }
      });

      return clonedScene;
    }
    return null;
  }, [gltf.scene]);

  return wireframeScene ? (
    <primitive
      ref={meshRef}
      object={wireframeScene}
      position={position}
      scale={scale}
    />
  ) : null;
}

function HeroScene() {
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Mobile optimized camera and positioning
  const cameraPosition = isMobile ? [1, 1, 3] : [2, 1, 3];
  const modelPosition = isMobile ? [0, -3.8, 1] : [3.6, -2.8, 0];
  const modelScale = isMobile ? 1.8 : 1.5;
  const canvasHeight = isMobile ? '100%' : '100vh';

  return (
    <Canvas 
      frameloop="demand"
      camera={{ position: cameraPosition, fov: 45 }}
      style={{ width: '100%', height: canvasHeight }}
    >
      <Suspense fallback={null}>
        <ambientLight intensity={1.5} />
        <directionalLight position={[10, 10, 5]} intensity={2.5} />
        
        <HumanBodyModel position={modelPosition} scale={modelScale} />
        
        <Environment preset="city" />
        <OrbitControls 
          enablePan={false} 
          enableZoom={false} 
          enableRotate={false}
          minDistance={12}
          maxDistance={12}
        />
      </Suspense>
    </Canvas>
  );
}

function Hero() {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handlePlatformClick = (platform) => {
    if (platform.status === 'live') {
      navigate(`/${platform.platformType}`);
    }
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  // --- Pure DOM search ---
  const searchRef = useRef(null);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const searchTimerRef = useRef(null);

  const destroyDropdown = useCallback(() => {
    if (dropdownRef.current) { dropdownRef.current.remove(); dropdownRef.current = null; }
  }, []);

  const createDropdownContainer = useCallback(() => {
    destroyDropdown();
    if (!searchRef.current) return null;
    const rect = searchRef.current.getBoundingClientRect();
    const div = document.createElement('div');
    Object.assign(div.style, {
      position: 'fixed', top: `${rect.bottom + 4}px`, left: `${rect.left}px`,
      width: `${rect.width}px`, background: '#fff', borderRadius: '8px',
      border: '1px solid #e2e8f0', maxHeight: '350px', overflowY: 'auto', zIndex: '2147483647',
    });
    document.body.appendChild(div);
    dropdownRef.current = div;
    return div;
  }, [destroyDropdown]);

  const showMessage = useCallback((text) => {
    const div = createDropdownContainer();
    if (!div) return;
    const msg = document.createElement('div');
    Object.assign(msg.style, { padding: '12px 16px', color: '#94a3b8', fontSize: '0.9rem' });
    msg.textContent = text;
    div.appendChild(msg);
  }, [createDropdownContainer]);

  const buildDropdown = useCallback((results) => {
    if (!results.length) { showMessage('No results found'); return; }
    const div = createDropdownContainer();
    if (!div) return;
    results.forEach((result, i) => {
      const row = document.createElement('div');
      Object.assign(row.style, {
        padding: '10px 16px', cursor: 'pointer', display: 'flex',
        justifyContent: 'space-between', alignItems: 'center',
        borderBottom: i < results.length - 1 ? '1px solid #f1f5f9' : 'none',
      });
      row.onmouseenter = () => { row.style.background = '#f8fafc'; };
      row.onmouseleave = () => { row.style.background = '#fff'; };
      row.onclick = () => {
        if (searchInputRef.current) searchInputRef.current.value = '';
        destroyDropdown();
        navigate('/wbt/explore', { state: { searchResult: result } });
      };
      const label = document.createElement('span');
      Object.assign(label.style, { fontWeight: '500', color: '#1e293b', fontSize: '0.9rem' });
      label.textContent = result.label;
      const badge = document.createElement('span');
      Object.assign(badge.style, {
        fontSize: '0.75rem', color: '#94a3b8', background: '#f1f5f9',
        padding: '2px 8px', borderRadius: '4px', textTransform: 'capitalize',
        flexShrink: '0', marginLeft: '12px',
      });
      badge.textContent = result.tab;
      row.appendChild(label);
      row.appendChild(badge);
      div.appendChild(row);
    });
  }, [createDropdownContainer, showMessage, destroyDropdown, navigate]);

  const handleSearchChange = useCallback((e) => {
    const query = e.target.value;
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (!query.trim()) { destroyDropdown(); return; }
    showMessage('Searching...');
    searchTimerRef.current = setTimeout(async () => {
      try {
        const res = await fetch(`${SEARCH_API_URL}/search?q=${encodeURIComponent(query.trim())}`);
        if (!res.ok) throw new Error('Search failed');
        buildDropdown(await res.json());
      } catch (err) { console.error('Search error:', err); destroyDropdown(); }
    }, 300);
  }, [buildDropdown, destroyDropdown, showMessage]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target) &&
        (!dropdownRef.current || !dropdownRef.current.contains(e.target))) {
        destroyDropdown();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [destroyDropdown]);

  useEffect(() => () => destroyDropdown(), [destroyDropdown]);

  return (
    <>
      {/* Simplified Navigation */}
      <div className="navigation-system">
        <div className="main-nav-container">
          {/* Logo */}
          <a href="/" className="main-logo">
            <img src={logoImage} alt="PhenomeAtlas" className="main-logo-image" />
          </a>

          {/* Platform Menu */}
          <nav className="platform-menu">
            <a href="/wbt" className="platform-link">WBT-Atlas</a>
            {isMobile ? (
              <a href="/contact" className="platform-link">Contact</a>
            ) : (
              <>
              <a href="/contact" className="platform-link">Contact</a>
              </>
            )}
          </nav>

          {/* Search Bar */}
          {!isMobile && (
            <div className="integrated-search" ref={searchRef}>
              <div className="search-input-wrapper">
                <svg className="search-icon" width="18" height="18" viewBox="0 0 20 20" fill="none">
                  <path d="M17.5 17.5L12.5 12.5M14.1667 8.33333C14.1667 11.555 11.555 14.1667 8.33333 14.1667C5.11167 14.1667 2.5 11.555 2.5 8.33333C2.5 5.11167 5.11167 2.5 8.33333 2.5C11.555 2.5 14.1667 5.11167 14.1667 8.33333Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <input
                  type="text"
                  ref={searchInputRef}
                  placeholder="Search genes, diseases, pathways..."
                  className="integrated-search-input"
                  onChange={handleSearchChange}
                />
                <button className="search-submit-btn-integrated">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M6.5 12L11 7.5L6.5 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            </div>
          )}

                     {/* Mobile Menu Button - only show on mobile */}
           {isMobile && (
             <button className={`mobile-menu-btn ${mobileMenuOpen ? 'active' : ''}`} onClick={toggleMobileMenu}>
               <span></span>
               <span></span>
               <span></span>
             </button>
           )}
        </div>
      </div>

      {/* Mobile Overlay */}
      {isMobile && (
        <>
          <div className={`mobile-overlay ${mobileMenuOpen ? 'open' : ''}`} onClick={closeMobileMenu}></div>
          
          {/* Mobile Sidebar */}
          <div className={`mobile-sidebar ${mobileMenuOpen ? 'open' : ''}`}>
            <div className="mobile-sidebar-content">
              <a href="/wbt" className="mobile-nav-link" onClick={closeMobileMenu}>WBT-Atlas</a>
              <a href="/contact" className="mobile-nav-link" onClick={closeMobileMenu}>Contact</a>
              <a href="https://mphenome.org" className="mobile-nav-link" onClick={closeMobileMenu}>About 1M-PI</a>
            </div>
          </div>
        </>
      )}

      <div className="hero-section">
        {/* Background mesh gradient */}
        <div className="mesh-gradient"></div>
        
        {/* Orbit Animation System - positioned behind the 3D scene */}
        <div className="orbit-container">
          <div className="orbit-path orbit-path-1">
            <div className="orbit-element orbit-element-1"></div>
          </div>
          
          <div className="orbit-path orbit-path-2">
            <div className="orbit-element orbit-element-2"></div>
          </div>
          
          <div className="orbit-path orbit-path-3">
            <div className="orbit-element orbit-element-3"></div>
          </div>
          
          <div className="orbit-path orbit-path-4">
            <div className="orbit-element orbit-element-4"></div>
          </div>
          
          <div className="orbit-path orbit-path-5">
            <div className="orbit-element orbit-element-5"></div>
          </div>

          <div className="orbit-path orbit-path-6">
            <div className="orbit-element orbit-element-6"></div>
          </div>
        </div>

        {/* Full page gradient overlay */}
        <div className="page-gradient-overlay"></div>
        
        {/* Canvas wrapper positioned according to device */}
        {!isMobile && (
          <div className="canvas-wrapper">
            <HeroScene />
          </div>
        )}
        
        {/* Hero Content */}
        <div className="hero-content">
          <div className="hero-badge">
            <span className="badge-dot"></span>
            Phenome Atlas
          </div>
          <h1 className="hero-header">
            One Million Phenome Initiative<br />
            <span className="hero-highlight"> Phenome Atlas</span>
          </h1>

          <p className="hero-subheader">
The One Million Phenome Initiative (1M-PI) is the world's largest multi-omics research initiative, designed to map molecular, cellular, and physiological features across one million individuals from diverse populations worldwide. This publicly accessible Phenome Atlas integrates genomics, transcriptomics, proteomics, metabolomics, lipidomics, metagenomics, and phenomics data, providing an unprecedented framework for understanding human biology, disease mechanisms, and biomarker discovery in the context of global population diversity. By bridging fundamental research and clinical applications, Phenome Atlas serves as a cornerstone for precision medicine, enabling the development of new diagnostics, targeted therapies, and strategies for disease prevention.</p>

          <div className="hero-cta">
            <button
              className="hero-button hero-button-primary"
              onClick={() => window.location.href = '/wbt'}
            >
              WBT Atlas 
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <button
              className="hero-button hero-button-secondary"
              onClick={() => window.location.href = 'https://mphenome.org'}
            >
              About 1M-PI
            </button>
          </div>
          
          <div className="hero-stats">
            <div className="stat">
              <div className="stat-value">1 Million</div>
              <div className="stat-label">Individuals From Diverse <br />Populations</div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat">
              <div className="stat-value">10+</div>
              <div className="stat-label">Omics Data<br /> Types</div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat">
              <div className="stat-value">8+</div>
              <div className="stat-label">Atlas for Exploring<br />   Omics Data</div>
            </div>
          </div>
        </div>

        {/* Canvas wrapper for mobile positioned after content */}
        {isMobile && (
          <div className="canvas-wrapper">
            <HeroScene />
          </div>
        )}
      </div>
    </>
  );
}

export default Hero;